// Dependency-free SVG chart + UI primitives for the Manager/Admin analytics
// surfaces (ManagerDashboard, ComplianceReport). The app has no charting library
// (see WaterRestrictionCityDrawer's inline donut) — these are the shared building
// blocks so both screens render the same way. React + lucide-react + Tailwind only.

import { useState, type ReactNode } from 'react';

// ---------------------------------------------------------------------------
// Donut — proportional ring. segments are drawn in order; zero-value omitted.
// ---------------------------------------------------------------------------
export interface DonutSegment {
  key: string;
  label: string;
  value: number;
  color: string;
}

export function Donut({
  segments,
  size = 132,
  thickness = 16,
  centerValue,
  centerLabel,
}: {
  segments: DonutSegment[];
  size?: number;
  thickness?: number;
  centerValue?: ReactNode;
  centerLabel?: string;
}) {
  const r = (size - thickness) / 2;
  const cx = size / 2;
  const C = 2 * Math.PI * r;
  const total = segments.reduce((s, x) => s + Math.max(0, x.value), 0);
  let offset = 0;
  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} className="shrink-0">
      <circle cx={cx} cy={cx} r={r} fill="none" stroke="var(--color-border)" strokeWidth={thickness} />
      {total > 0 &&
        segments.map((s) => {
          const val = Math.max(0, s.value);
          if (val === 0) return null;
          const len = (val / total) * C;
          const el = (
            <circle
              key={s.key}
              cx={cx}
              cy={cx}
              r={r}
              fill="none"
              stroke={s.color}
              strokeWidth={thickness}
              strokeDasharray={`${len} ${C - len}`}
              strokeDashoffset={-offset}
              transform={`rotate(-90 ${cx} ${cx})`}
              strokeLinecap="butt"
            >
              <title>{`${s.label}: ${val}`}</title>
            </circle>
          );
          offset += len;
          return el;
        })}
      {(centerValue != null || centerLabel) && (
        <>
          <text x={cx} y={cx - 2} textAnchor="middle" className="font-bold" style={{ fontSize: size * 0.18, fill: 'var(--color-text-primary)' }}>
            {centerValue}
          </text>
          {centerLabel && (
            <text x={cx} y={cx + size * 0.13} textAnchor="middle" style={{ fontSize: size * 0.072, fill: 'var(--color-text-secondary)' }}>
              {centerLabel}
            </text>
          )}
        </>
      )}
    </svg>
  );
}

/** Color swatch + label + value legend, pairs with a Donut. */
export function Legend({
  segments,
  total,
  onSelect,
  selectedKey,
}: {
  segments: DonutSegment[];
  total?: number;
  onSelect?: (key: string) => void;
  selectedKey?: string | null;
}) {
  const sum = total ?? segments.reduce((s, x) => s + Math.max(0, x.value), 0);
  return (
    <ul className="flex flex-col gap-1.5 text-sm">
      {segments.map((s) => {
        const pct = sum > 0 ? Math.round((s.value / sum) * 100) : 0;
        const row = (
          <span className="flex items-center gap-2 w-full">
            <span className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ background: s.color }} />
            <span className="text-theme-secondary truncate">{s.label}</span>
            <span className="ml-auto font-semibold text-theme-primary tabular-nums">{s.value}</span>
            <span className="w-9 text-right text-xs text-theme-secondary tabular-nums">{pct}%</span>
          </span>
        );
        return (
          <li key={s.key}>
            {onSelect ? (
              <button
                type="button"
                onClick={() => onSelect(s.key)}
                className={`flex w-full items-center rounded-md px-1.5 py-1 text-left transition-colors ${
                  selectedKey === s.key ? 'bg-subtle' : 'hover:bg-subtle'
                }`}
              >
                {row}
              </button>
            ) : (
              <div className="px-1.5 py-1">{row}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Horizontal bar list — labelled bars, good for "by city" / "by type".
// ---------------------------------------------------------------------------
export interface BarDatum {
  key: string;
  label: string;
  value: number;
  color?: string;
  sub?: string;
}

export function BarList({
  data,
  max,
  onSelect,
  valueSuffix = '',
  emptyText = '—',
}: {
  data: BarDatum[];
  max?: number;
  onSelect?: (key: string) => void;
  valueSuffix?: string;
  emptyText?: string;
}) {
  if (data.length === 0) return <p className="text-sm text-theme-secondary py-4 text-center">{emptyText}</p>;
  const peak = max ?? Math.max(1, ...data.map((d) => d.value));
  return (
    <div className="flex flex-col gap-2">
      {data.map((d) => {
        const w = Math.max(2, Math.round((d.value / peak) * 100));
        const inner = (
          <>
            <div className="flex items-baseline justify-between gap-2 mb-0.5">
              <span className="text-sm text-theme-secondary truncate">{d.label}</span>
              <span className="text-sm font-semibold text-theme-primary tabular-nums shrink-0">
                {d.value}
                {valueSuffix}
                {d.sub && <span className="ml-1 text-xs font-normal text-theme-secondary">{d.sub}</span>}
              </span>
            </div>
            <div className="h-2 w-full rounded-full bg-subtle overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${w}%`, background: d.color ?? '#3b82f6' }} />
            </div>
          </>
        );
        return onSelect ? (
          <button key={d.key} type="button" onClick={() => onSelect(d.key)} className="text-left rounded-md p-1 -m-1 hover:bg-subtle">
            {inner}
          </button>
        ) : (
          <div key={d.key}>{inner}</div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Trend chart — multi-series line over shared categories (e.g. weekly).
// ---------------------------------------------------------------------------
export interface TrendSeries {
  key: string;
  label: string;
  color: string;
  points: number[];
}

export function TrendChart({
  series,
  categories,
  height = 160,
}: {
  series: TrendSeries[];
  categories: string[];
  height?: number;
}) {
  const W = 560;
  const H = height;
  const padL = 28;
  const padR = 8;
  const padT = 10;
  const padB = 22;
  const n = categories.length;
  const allVals = series.flatMap((s) => s.points);
  const maxV = Math.max(1, ...allVals);
  const x = (i: number) => padL + (n <= 1 ? 0 : (i / (n - 1)) * (W - padL - padR));
  const y = (v: number) => padT + (1 - v / maxV) * (H - padT - padB);
  const [hover, setHover] = useState<number | null>(null);
  const ticks = 3;
  return (
    <div className="w-full overflow-hidden">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height }} preserveAspectRatio="none"
        onMouseLeave={() => setHover(null)}>
        {Array.from({ length: ticks + 1 }).map((_, i) => {
          const v = (maxV / ticks) * i;
          const yy = y(v);
          return (
            <g key={i}>
              <line x1={padL} y1={yy} x2={W - padR} y2={yy} stroke="var(--color-border)" strokeWidth={1} />
              <text x={2} y={yy + 3} style={{ fontSize: 8, fill: 'var(--color-text-secondary)' }}>{Math.round(v)}</text>
            </g>
          );
        })}
        {series.map((s) => {
          const d = s.points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(p).toFixed(1)}`).join(' ');
          return (
            <g key={s.key}>
              <path d={d} fill="none" stroke={s.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
              {s.points.map((p, i) => (
                <circle key={i} cx={x(i)} cy={y(p)} r={hover === i ? 3.5 : 2} fill={s.color} />
              ))}
            </g>
          );
        })}
        {categories.map((_, i) => (
          <rect key={i} x={x(i) - (W / n) / 2} y={0} width={W / n} height={H} fill="transparent" onMouseEnter={() => setHover(i)} />
        ))}
        {hover != null && (
          <line x1={x(hover)} y1={padT} x2={x(hover)} y2={H - padB} stroke="#cbd5e1" strokeWidth={1} strokeDasharray="3 3" />
        )}
        {categories.map((c, i) =>
          i % Math.ceil(n / 6) === 0 || i === n - 1 ? (
            <text key={i} x={x(i)} y={H - 6} textAnchor="middle" style={{ fontSize: 8, fill: 'var(--color-text-secondary)' }}>{c}</text>
          ) : null,
        )}
      </svg>
      {hover != null && (
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-theme-secondary -mt-1">
          <span className="font-medium text-theme-secondary">{categories[hover]}</span>
          {series.map((s) => (
            <span key={s.key} className="inline-flex items-center gap-1">
              <span className="h-2 w-2 rounded-sm" style={{ background: s.color }} />
              {s.label}: <span className="font-semibold tabular-nums">{s.points[hover]}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Grouped bar chart — same series/categories shape as TrendChart, drawn as
// side-by-side bars per category (easier to compare two series, e.g. created
// vs approved per week). Legend is always visible; values show on hover.
// ---------------------------------------------------------------------------
export function GroupedBarChart({
  series,
  categories,
  height = 160,
}: {
  series: TrendSeries[];
  categories: string[];
  height?: number;
}) {
  const W = 560;
  const H = height;
  const padL = 28;
  const padR = 8;
  const padT = 10;
  const padB = 22;
  const n = categories.length;
  const maxV = Math.max(1, ...series.flatMap((s) => s.points));
  const y = (v: number) => padT + (1 - v / maxV) * (H - padT - padB);
  const baseY = y(0);
  const plotW = W - padL - padR;
  const groupW = n > 0 ? plotW / n : plotW;
  const innerPad = groupW * 0.18;
  const barW = series.length > 0 ? (groupW - innerPad * 2) / series.length : groupW;
  const groupX = (i: number) => padL + i * groupW;
  const [hover, setHover] = useState<number | null>(null);
  const ticks = 3;
  return (
    <div className="w-full overflow-hidden">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height }} preserveAspectRatio="none"
        onMouseLeave={() => setHover(null)}>
        {Array.from({ length: ticks + 1 }).map((_, i) => {
          const v = (maxV / ticks) * i;
          const yy = y(v);
          return (
            <g key={i}>
              <line x1={padL} y1={yy} x2={W - padR} y2={yy} stroke="var(--color-border)" strokeWidth={1} />
              <text x={2} y={yy + 3} style={{ fontSize: 8, fill: 'var(--color-text-secondary)' }}>{Math.round(v)}</text>
            </g>
          );
        })}
        {categories.map((_, i) => {
          const gx = groupX(i) + innerPad;
          return (
            <g key={i}>
              {series.map((s, si) => {
                const v = s.points[i] ?? 0;
                const by = y(v);
                return (
                  <rect
                    key={s.key}
                    x={gx + si * barW + 0.5}
                    y={by}
                    width={Math.max(0, barW - 1)}
                    height={Math.max(0, baseY - by)}
                    fill={s.color}
                    opacity={hover == null || hover === i ? 1 : 0.4}
                    rx={1}
                  />
                );
              })}
              <rect x={groupX(i)} y={0} width={groupW} height={H} fill="transparent" onMouseEnter={() => setHover(i)} />
            </g>
          );
        })}
        {categories.map((c, i) =>
          i % Math.ceil(n / 6) === 0 || i === n - 1 ? (
            <text key={i} x={groupX(i) + groupW / 2} y={H - 6} textAnchor="middle" style={{ fontSize: 8, fill: 'var(--color-text-secondary)' }}>{c}</text>
          ) : null,
        )}
      </svg>
      <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-theme-secondary -mt-1">
        {hover != null && <span className="font-medium text-theme-secondary">{categories[hover]}</span>}
        {series.map((s) => (
          <span key={s.key} className="inline-flex items-center gap-1">
            <span className="h-2 w-2 rounded-sm" style={{ background: s.color }} />
            {s.label}
            {hover != null ? <>: <span className="font-semibold tabular-nums">{s.points[hover] ?? 0}</span></> : null}
          </span>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// StatTile — KPI tile; optionally clickable (drill-down).
// ---------------------------------------------------------------------------
export type Tone = 'blue' | 'emerald' | 'amber' | 'red' | 'gray' | 'sky' | 'violet';

// Accent hue per tone. The tile itself is a themed (bg-surface) tile that
// blends with the active theme; the accent shows up only as the small icon and
// the KPI value, so on the dark theme nothing reads as a bright white block.
// These mid (~500) shades keep adequate contrast on both light and dark surfaces.
const TONE_ACCENT: Record<Tone, string> = {
  blue: '#3b82f6',
  emerald: '#10b981',
  amber: '#f59e0b',
  red: '#ef4444',
  gray: '#6b7280',
  sky: '#0ea5e9',
  violet: '#8b5cf6',
};

export function StatTile({
  icon,
  label,
  value,
  sub,
  tone = 'gray',
  onClick,
  title,
}: {
  icon?: ReactNode;
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: Tone;
  onClick?: () => void;
  title?: string;
}) {
  const accent = TONE_ACCENT[tone];
  const body = (
    <>
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-theme-secondary">{label}</span>
        {icon && (
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-subtle" style={{ color: accent }}>
            {icon}
          </span>
        )}
      </div>
      <div className="mt-1 text-2xl font-bold tabular-nums" style={{ color: accent }}>{value}</div>
      {sub && <div className="mt-0.5 text-xs text-theme-secondary">{sub}</div>}
    </>
  );
  const cls = 'rounded-xl border border-line bg-surface px-3.5 py-3';
  return onClick ? (
    <button type="button" onClick={onClick} title={title} className={`${cls} text-left transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-400`}>
      {body}
    </button>
  ) : (
    <div className={cls} title={title}>{body}</div>
  );
}

// ---------------------------------------------------------------------------
// Pill / badge
// ---------------------------------------------------------------------------
export function Pill({ children, tone = 'gray', icon }: { children: ReactNode; tone?: Tone; icon?: ReactNode }) {
  const map: Record<Tone, string> = {
    blue: 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300',
    emerald: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300',
    amber: 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300',
    red: 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300',
    gray: 'bg-subtle text-theme-secondary',
    sky: 'bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300',
    violet: 'bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-300',
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${map[tone]}`}>
      {icon}
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Tooltip — lightweight hover bubble (the app otherwise uses native title=).
// ---------------------------------------------------------------------------
export function Tooltip({ content, children }: { content: ReactNode; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-flex" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      {children}
      {open && (
        <span className="pointer-events-none absolute bottom-full left-1/2 z-50 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs text-white shadow-lg">
          {content}
          <span className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
        </span>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Card — standard panel wrapper used across the dashboard.
// ---------------------------------------------------------------------------
export function Card({
  title,
  icon,
  action,
  children,
  className = '',
  bodyClassName = 'p-4',
}: {
  title?: ReactNode;
  icon?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <div className={`rounded-xl border border-line bg-surface shadow-sm ${className}`}>
      {(title || action) && (
        <div className="flex items-center gap-2 border-b border-line px-4 py-2.5">
          {icon && <span className="text-theme-secondary">{icon}</span>}
          {title && <h3 className="text-sm font-semibold text-theme-primary">{title}</h3>}
          {action && <div className="ml-auto">{action}</div>}
        </div>
      )}
      <div className={bodyClassName}>{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tabs — segmented control.
// ---------------------------------------------------------------------------
export interface TabDef {
  key: string;
  label: string;
  icon?: ReactNode;
  badge?: ReactNode;
}

export function Tabs({ tabs, active, onChange }: { tabs: TabDef[]; active: string; onChange: (k: string) => void }) {
  return (
    <div className="inline-flex flex-wrap gap-1 rounded-xl bg-subtle p-1">
      {tabs.map((t) => (
        <button
          key={t.key}
          type="button"
          onClick={() => onChange(t.key)}
          className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
            active === t.key ? 'bg-surface text-theme-primary shadow-sm' : 'text-theme-secondary hover:bg-subtle'
          }`}
        >
          {t.icon}
          {t.label}
          {t.badge != null && (
            <span className={`ml-0.5 rounded-full px-1.5 text-xs ${active === t.key ? 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300' : 'bg-subtle text-theme-secondary'}`}>
              {t.badge}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}
